import type { MeResponse } from '@complan/contracts';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { hasCapability } from '@/app/session/session';
import { filterContextHref } from '@/shared/url/filter-context';
import { useFilterContext } from '@/shared/url/use-filter-context';
import { NAVIGATION, type NavItem } from './navigation';

/**
 * The fixed 212px navy sidebar (UI spec §2, §2.1).
 *
 * Anatomy held to the spec: a brand block with the "Commercial Planning" eyebrow
 * over the "ComPlan" wordmark; the five binding navigation groups in order; a
 * pinned footer version note. The prototype role selector and the dismissible
 * banner are deliberately absent (§0.1) — role comes from the session, not a
 * switcher.
 *
 * Edit-gated items render at opacity .35 for principals without the gating
 * capability (§2.1). Placeholder items (deferred screens, §0.1) render as inert
 * text so the shell reads as the whole product without offering navigation
 * Foundation has not built.
 */
export function Sidebar({ me }: { me: MeResponse | undefined }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <aside className="bg-navy sticky top-0 flex h-screen w-[212px] shrink-0 flex-col overflow-y-auto">
      <BrandBlock />
      <nav className="flex-1 py-2" aria-label={t('nav.ariaLabel')}>
        {NAVIGATION.map((group) => (
          <div key={group.labelKey} className="mb-1 px-2 py-2">
            <p className="text-sidebar-group px-2 pb-1 text-[10px] font-semibold uppercase tracking-[1.1px]">
              {t(group.labelKey)}
            </p>
            <ul>
              {group.items.map((item) => (
                <li key={item.labelKey}>
                  <NavItemRow item={item} me={me} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <SidebarFooter />
    </aside>
  );
}

function BrandBlock(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="border-b border-white/[0.12] px-4 py-3">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.6px] text-[color:var(--sidebar-eyebrow)]">
        {t('app.eyebrow')}
      </p>
      <p className="text-[15px] font-semibold text-white">{t('app.name')}</p>
    </div>
  );
}

function NavItemRow({ item, me }: { item: NavItem; me: MeResponse | undefined }): React.JSX.Element {
  const { t } = useTranslation();
  const Icon = item.icon;
  // R6.3: leaving a filtered screen for another that shares the Filter_Context
  // carries the selection, the planning year and the sort. A constant `to` would
  // discard them, which is what this replaces. `undefined` on either side - the
  // current screen carries no context, or this item is not a participant - leaves the
  // constant target exactly as it was.
  const context = useFilterContext();
  const target =
    item.filterScreen !== undefined && context !== undefined
      ? filterContextHref(item.filterScreen, context)
      : item.to;
  // §2.1: the gate dims the item; it does not remove it, so the product's shape
  // stays legible to every role.
  const gatedOut = item.editGatedCapability
    ? !hasCapability(me, item.editGatedCapability)
    : false;

  const base =
    'flex items-center gap-2 border-l-[3px] px-4 py-[9px] text-[12.5px] text-sidebar-item transition-colors';

  if (item.kind === 'placeholder' || target === undefined) {
    // Deferred screen (§0.1): visible, not interactive, and never a link target.
    return (
      <span
        aria-disabled="true"
        title={t('nav.placeholderHint')}
        className={cn(
          base,
          'cursor-default border-transparent',
          gatedOut && 'opacity-35',
          'opacity-60',
        )}
      >
        <Icon aria-hidden className="size-4" />
        <span>{t(item.labelKey)}</span>
      </span>
    );
  }

  return (
    <NavLink
      to={target}
      className={({ isActive }) =>
        cn(
          base,
          'hover:bg-white/[0.06] hover:text-white',
          isActive
            ? 'border-l-accent bg-white/10 font-semibold text-white'
            : 'border-transparent',
          gatedOut && 'opacity-35',
        )
      }
    >
      <Icon aria-hidden className="size-4" />
      <span>{t(item.labelKey)}</span>
    </NavLink>
  );
}

function SidebarFooter(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="bg-navy sticky bottom-0 border-t border-white/[0.12] px-4 py-3">
      <p className="text-[10.5px] text-[color:var(--sidebar-group)]">{t('app.footer')}</p>
    </div>
  );
}
